/**
 * Status-overlay copy for a soft-deferred master playlist swap (hard-coded PT like CurrentState).
 */

import { formatContentChangeEtaRemaining } from '@/helpers/formatContentChangeEtaRemaining'

export type TPendingPlaylistSwapOverlayPhase = 'downloading' | 'ready_at_boundary' | 'rematching'

export type TPendingPlaylistSwapOverlayInput = {
  title: string
  progress01: number
  phase: TPendingPlaylistSwapOverlayPhase
  /** Absolute wall time when soft-defer force/cutover is expected; drives live countdown. */
  etaAtMs?: number | null
}

export type TPendingPlaylistSwapOverlayLine = {
  label: string
  value: string
  tone: 'normal' | 'warning'
}

/** Percent for the downloading cue; clamps to 0–100. */
export function formatPendingPlaylistSwapProgressPercent(progress01: number): number {
  if (!Number.isFinite(progress01)) {
    return 0
  }
  return Math.max(0, Math.min(100, Math.round(progress01 * 100)))
}

/**
 * Org-row line for a pending playlist assign while the on-air feed keeps playing.
 * Downloading: `Atualiz.` → `{title} · preparando {n}%` (no flush ETA — cutover waits for ready).
 * Ready / rematching: `Atualiz.` → `{title} · após este quadro · ~Xs` (ETA when known)
 */
export function resolvePendingPlaylistSwapOverlayLine(
  swap: TPendingPlaylistSwapOverlayInput,
  nowMs: number = Date.now()
): TPendingPlaylistSwapOverlayLine {
  const title = swap.title.trim() !== '' ? swap.title.trim() : 'Playlist'
  if (swap.phase === 'ready_at_boundary' || swap.phase === 'rematching') {
    const eta = formatContentChangeEtaRemaining(swap.etaAtMs, nowMs)
    const value = eta != null
      ? `${title} · após este quadro · ${eta}`
      : `${title} · após este quadro`
    return {
      label: 'Atualiz.',
      value,
      tone: 'warning'
    }
  }
  const pct = formatPendingPlaylistSwapProgressPercent(swap.progress01)
  return {
    label: 'Atualiz.',
    value: `${title} · preparando ${pct}%`,
    tone: 'normal'
  }
}
